
import { useState } from 'react';

const EVENT_CATEGORIES = [
  'Arts & Culture', 'Community', 'Education', 'Family & Kids',
  'Festival', 'Food & Drink', 'Health & Wellness', 'Music',
  'Networking', 'Outdoors & Nature', 'Sports & Fitness', 'Workshop'
];

const ADMISSION_TYPES = ['Free', 'Paid', 'Donation-based', 'Registration Required'];

export default function EventSubmissionForm() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    const form = e.currentTarget;
    const data = new FormData(form);
    const body = new URLSearchParams();
    data.forEach((value, key) => body.append(key, value.toString()));

    try {
      const res = await fetch('https://readdy.ai/api/form/d6jni2apaf6ievb71ip0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (res.ok) {
        setSubmitted(true);
        form.reset();
        setCharCount(0);
      } else {
        setError('Something went wrong. Please try again.');
      }
    } catch {
      setError('Network error. Please check your connection and try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-12 text-center">
        <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-5">
          <i className="ri-check-line text-teal-600 text-3xl"></i>
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-3">Event Submitted!</h2>
        <p className="text-gray-600 mb-2 max-w-md mx-auto">
          Thank you for submitting your event. Our team will review it within 24 hours and add it to the Roseville community calendar.
        </p>
        <p className="text-sm text-gray-500 mb-8">You'll receive a confirmation email once your event is approved.</p>
        <div className="flex flex-wrap justify-center gap-3">
          <button
            onClick={() => setSubmitted(false)}
            className="px-6 py-2.5 bg-teal-600 text-white rounded-lg font-medium hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap"
          >
            Submit Another Event
          </button>
          <a
            href="/calendar"
            className="px-6 py-2.5 border border-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors whitespace-nowrap"
          >
            View Events Calendar
          </a>
        </div>
      </div>
    );
  }

  return (
    <form
      data-readdy-form
      id="submit-event-form"
      onSubmit={handleSubmit}
      className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 space-y-8"
    >
      {/* Event Basics */}
      <div>
        <h2 className="text-lg font-bold text-gray-900 mb-5 flex items-center gap-2">
          <span className="w-7 h-7 bg-teal-600 text-white rounded-full flex items-center justify-center text-sm font-bold">1</span>
          Event Basics
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Event Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="event_name"
              required
              placeholder="e.g. Roseville Summer Farmers Market"
              className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Category <span className="text-red-500">*</span>
            </label>
            <select
              name="category"
              required
              className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white cursor-pointer"
            >
              <option value="">Select a category</option>
              {EVENT_CATEGORIES.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Admission <span className="text-red-500">*</span>
            </label>
            <select
              name="admission"
              required
              className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white cursor-pointer"
            >
              <option value="">Select admission type</option>
              {ADMISSION_TYPES.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Event Description <span className="text-red-500">*</span>
            </label>
            <textarea
              name="description"
              required
              rows={5}
              maxLength={500}
              placeholder="Describe your event — what to expect, who it's for, highlights, etc."
              className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent resize-none"
              onChange={e => setCharCount(e.target.value.length)}
            />
            <p className="text-xs text-gray-400 mt-1 text-right">{charCount}/500 characters</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Ticket / Admission Price
            </label>
            <input
              type="text"
              name="price"
              placeholder="e.g. $10, Free, $5–$20"
              className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Age Group
            </label>
            <select
              name="age_group"
              className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white cursor-pointer"
            >
              <option value="">All Ages</option>
              <option value="Kids (0–12)">Kids (0–12)</option>
              <option value="Teens (13–17)">Teens (13–17)</option>
              <option value="Adults (18+)">Adults (18+)</option>
              <option value="Seniors (55+)">Seniors (55+)</option>
              <option value="Families">Families</option>
            </select>
          </div>
        </div>
      </div>

      {/* Date & Time */}
      <div>
        <h2 className="text-lg font-bold text-gray-900 mb-5 flex items-center gap-2">
          <span className="w-7 h-7 bg-teal-600 text-white rounded-full flex items-center justify-center text-sm font-bold">2</span>
          Date &amp; Time
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Start Date <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              name="start_date"
              required
              className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent cursor-pointer"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              End Date
            </label>
            <input
              type="date"
              name="end_date"
              className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent cursor-pointer"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Start Time <span className="text-red-500">*</span>
            </label>
            <input
              type="time"
              name="start_time"
              required
              className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent cursor-pointer"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              End Time
            </label>
            <input
              type="time"
              name="end_time"
              className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent cursor-pointer"
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Recurring Event?
            </label>
            <select
              name="recurring"
              className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white cursor-pointer"
            >
              <option value="One-time">One-time event</option>
              <option value="Weekly">Weekly</option>
              <option value="Bi-weekly">Bi-weekly</option>
              <option value="Monthly">Monthly</option>
            </select>
          </div>
        </div>
      </div>

      {/* Location */}
      <div>
        <h2 className="text-lg font-bold text-gray-900 mb-5 flex items-center gap-2">
          <span className="w-7 h-7 bg-teal-600 text-white rounded-full flex items-center justify-center text-sm font-bold">3</span>
          Location
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Venue Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="venue_name"
              required
              placeholder="e.g. Maidu Regional Park, Roseville Civic Center"
              className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Street Address <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="address"
              required
              placeholder="e.g. 1550 Maidu Dr, Roseville, CA 95661"
              className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Parking Info
            </label>
            <input
              type="text"
              name="parking"
              placeholder="e.g. Free parking on-site"
              className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Virtual / Online Event?
            </label>
            <select
              name="virtual"
              className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white cursor-pointer"
            >
              <option value="In-person">In-person only</option>
              <option value="Virtual">Virtual only</option>
              <option value="Hybrid">Hybrid (in-person + virtual)</option>
            </select>
          </div>
        </div>
      </div>

      {/* Organizer Info */}
      <div>
        <h2 className="text-lg font-bold text-gray-900 mb-5 flex items-center gap-2">
          <span className="w-7 h-7 bg-teal-600 text-white rounded-full flex items-center justify-center text-sm font-bold">4</span>
          Organizer Info
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Organizer / Organization Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="organizer_name"
              required
              placeholder="e.g. Roseville Parks & Recreation"
              className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Contact Email <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              name="email"
              required
              placeholder="you@example.com"
              className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Contact Phone
            </label>
            <input
              type="tel"
              name="phone"
              placeholder="(916) 555-0000"
              className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Event Website / Tickets URL
            </label>
            <input
              type="url"
              name="website"
              placeholder="https://..."
              className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Event Image URL
            </label>
            <input
              type="url"
              name="image_url"
              placeholder="https://... (link to a photo for your event listing)"
              className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            />
            <p className="text-xs text-gray-400 mt-1">Provide a direct link to an image (JPG or PNG). Recommended size: 1200×630px.</p>
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Additional Notes
            </label>
            <textarea
              name="notes"
              rows={3}
              maxLength={500}
              placeholder="Anything else you'd like us to know about this event?"
              className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent resize-none"
            />
          </div>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm">
          <i className="ri-error-warning-line text-lg"></i>
          {error}
        </div>
      )}

      <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-2 border-t border-gray-100">
        <p className="text-xs text-gray-400">
          <span className="text-red-500">*</span> Required fields. Events are reviewed within 24 hours.
        </p>
        <button
          type="submit"
          disabled={submitting}
          className="px-8 py-3 bg-teal-600 text-white rounded-lg font-semibold hover:bg-teal-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap flex items-center gap-2"
        >
          {submitting ? (
            <>
              <i className="ri-loader-4-line animate-spin"></i>
              Submitting...
            </>
          ) : (
            <>
              <i className="ri-send-plane-line"></i>
              Submit Event
            </>
          )}
        </button>
      </div>
    </form>
  );
}
